import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TRANSLATIONS = {
  en: {
    title: "Forgot Password",
    subtitle: "Enter your email and we'll send you a reset link",
    email: "Email Address",
    emailPlaceholder: "your@email.com",
    submit: "Send Reset Link",
    sending: "Sending...",
    back: "Back to Login",
    successTitle: "Check Your Email",
    successMessage: "If an account exists with this email, you will receive a password reset link.",
    tryAgain: "Try another email"
  },
  ms: {
    title: "Lupa Kata Laluan",
    subtitle: "Masukkan emel anda dan kami akan hantar pautan tetapan semula",
    email: "Alamat Emel",
    emailPlaceholder: "emel@anda.com",
    submit: "Hantar Pautan",
    sending: "Menghantar...",
    back: "Kembali ke Log Masuk",
    successTitle: "Semak Emel Anda",
    successMessage: "Jika akaun wujud dengan emel ini, anda akan menerima pautan tetapan semula kata laluan.",
    tryAgain: "Cuba emel lain"
  }
};

function getSavedLanguage() {
  return localStorage.getItem('myshield_lang') || 'en';
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const lang = getSavedLanguage();
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("Reset link sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-[#0A1128] to-black p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/login")}
        className="absolute top-4 left-4 gap-2 z-10 text-white/60 hover:text-white hover:bg-white/10"
        data-testid="back-to-login-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {t.back}
      </Button>
      
      <div className="w-full max-w-md glass-card p-8 relative z-10 animate-fade-in" data-testid="forgot-password-card">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
            alt="MyShield Logo" 
            className="h-16 w-auto mx-auto mb-4"
          />
          
          {!submitted ? (
            <>
              <h1 className="text-2xl font-bold text-white">{t.title}</h1>
              <p className="text-white/50 text-sm mt-2">{t.subtitle}</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">{t.successTitle}</h1>
              <p className="text-white/50 text-sm mt-2">{t.successMessage}</p>
            </>
          )}
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-white/70">{t.email}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                  className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl transition-all active:scale-[0.98]"
              disabled={loading}
              data-testid="forgot-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t.sending}
                </>
              ) : (
                t.submit
              )}
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <Button
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="w-full h-12 bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-xl"
              data-testid="try-again-button"
            >
              {t.tryAgain}
            </Button>
            <Link to="/login">
              <Button
                variant="ghost"
                className="w-full h-12 text-cyan-400 hover:text-cyan-300 hover:bg-white/5 rounded-xl"
              >
                {t.back}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
